import * as _ from 'lodash';
import * as XLSX from 'xlsx';
//import * as XLSX_ORG from 'xlsx';

const MONDAY_LOGO_FILE_PATH = 'xl/media/image1.png';
const MONDAY_LOGO_FILE_SIZE = 11186;

export type FormatVersion = 'v1' | 'v2' | 'unknown';

type CellTransportFormat = {
  value: any;
  original: XLSX.CellObject | undefined;
};

type ItemTransportFormat = {
  group: string;
  name: string;
  cells: CellTransportFormat[];
  level?: number;
};

type SubitemTransportFormat = ItemTransportFormat & {
  parentIdx: number;
};

export type BoardTransportFormat = {
  name: string;
  headlines: string[];
  subitemsHeadlines: string[] | null;
  items: ItemTransportFormat[];
  subitems: SubitemTransportFormat[] | null;
};

function getRowValues(mondaySheet: XLSX.WorkSheet, startColumn = 0, row = 2) {
  const values = [];
  let currentColumn = startColumn;

  while (true) {
    const cell = mondaySheet[XLSX.utils.encode_cell({ c: currentColumn++, r: row })];

    if (!cell) {
      break;
    }

    values.push(cell.v);
  }

  return values;
}

export function getBoardName(mondayWorkbook: XLSX.WorkBook) {
  const mondaySheet = mondayWorkbook.Sheets[mondayWorkbook.SheetNames[0]];
  return mondaySheet['A1']?.v;
}

function rowToItemTransport(mondaySheet: XLSX.WorkSheet, groupName: string | null, startColumn: number, row: number, columnsCount: number) {
  const itemTransport: ItemTransportFormat = {
    group: groupName || '',
    name: '',
    cells: [],
  };

  for (let currentColumn = startColumn; currentColumn < startColumn + columnsCount; currentColumn++) {
    const cell = mondaySheet[XLSX.utils.encode_cell({ c: currentColumn, r: row })];

    if (currentColumn === startColumn) {
      itemTransport.name = cell?.v;
    }

    itemTransport.cells.push({
      value: cell?.v,
      original: cell,
    });
  }

  return itemTransport;
}

function hasFormulasInRow(mondaySheet: XLSX.WorkSheet, row: number) {
  const range = XLSX.utils.decode_range(mondaySheet['!ref']!);
  const columnsCount = range.e.c + 1;

  for (let currentColumn = 0; currentColumn < columnsCount; currentColumn++) {
    const cell = mondaySheet[XLSX.utils.encode_cell({ c: currentColumn, r: row })];
    if (cell?.f) {
      return true;
    }
  }

  return false;
}

function isSummaryRowV2(mondaySheet: XLSX.WorkSheet, row: number): boolean {
  // Summary rows in v2 have EFEFEF background color
  const range = XLSX.utils.decode_range(mondaySheet['!ref']!);

  for (let c = 0; c <= range.e.c; c++) {
    const cell = mondaySheet[XLSX.utils.encode_cell({ c, r: row })];

    if (c === 0 && (!cell || !cell.v)) {
      // First cell must be empty for summary rows
      return false;
    }

    const bgColor = _.get(cell, 's.fgColor.rgb')?.toUpperCase();
    if (bgColor === 'EFEFEF') {
      return true;
    }
  }

  return false;
}

export function getMondayWorkbookCharacteristics(mondayWorkbook: XLSX.WorkBook) {
  const mondaySheet = mondayWorkbook.Sheets[mondayWorkbook.SheetNames[0]];
  const mondayLogoFile = _.get(mondayWorkbook, ['files', MONDAY_LOGO_FILE_PATH]);
  const hasMondayLogo = !!(mondayLogoFile && mondayLogoFile.size === MONDAY_LOGO_FILE_SIZE);

  // V1 detection
  const hasBoardDescriptionV1 = mondaySheet['A3']?.v === '';
  const firstGroupRowIdxV1 = hasBoardDescriptionV1 ? 3 : 1;
  const firstHeadlineCellV1 = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: firstGroupRowIdxV1 + 1 })];
  const columnHeadlineIsGreyV1 = _.get(firstHeadlineCellV1, 's.fgColor.rgb')?.toUpperCase() === 'D6D6D6';

  // V2 detection: no logo + white background on A1
  const firstCellBgColor = _.get(mondaySheet['A1'], 's.fgColor.rgb')?.toUpperCase();
  const firstCellHasWhiteBg = firstCellBgColor === 'FFFFFF';

  // For v2, find the first row with grey headers to determine structure
  let columnHeadlineIsGreyV2 = false;
  let columnHeadlineIsGreyMultiLevelSubitemsV2 = false;
  let firstGroupRowIdxV2 = 1;

  if (!hasMondayLogo && firstCellHasWhiteBg) {
    // Search for the first row with grey headers (within first 10 rows)
    for (let r = 1; r <= 10; r++) {
      const cell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: r })];
      const cellBg = _.get(cell, 's.fgColor.rgb')?.toUpperCase();

      if (cellBg === 'D6D6D6') {
        columnHeadlineIsGreyV2 = true;
        firstGroupRowIdxV2 = r - 1; // Group title is the row before headers
        break;
      }

      if (cellBg === 'EFEFEF') {
        // Check for multi-level subitems header row (EFEFEF background)
        columnHeadlineIsGreyMultiLevelSubitemsV2 = true;
        firstGroupRowIdxV2 = r - 1; // Group title is the row before headers
        break;
      }
    }
  }

  // Determine format version
  // Check v2 first (no logo + white A1 background + grey headers)
  let formatVersion: FormatVersion = 'unknown';
  if (!hasMondayLogo && firstCellHasWhiteBg && (columnHeadlineIsGreyMultiLevelSubitemsV2 || columnHeadlineIsGreyV2)) {
    formatVersion = 'v2';
  } else if (hasMondayLogo || columnHeadlineIsGreyV1) {
    formatVersion = 'v1';
  }

  // Board description detection depends on format version
  let hasBoardDescription = false;
  if (formatVersion === 'v1') {
    hasBoardDescription = hasBoardDescriptionV1;
  } else if (formatVersion === 'v2') {
    // In v2, if group title row is at row 3 or later, there's a description in row 1
    hasBoardDescription = firstGroupRowIdxV2 >= 3;
  }

  return {
    hasMondayLogo,
    hasBoardDescription,
    columnHeadlineIsGrey: formatVersion === 'v1' ? columnHeadlineIsGreyV1 : columnHeadlineIsGreyV2,
    formatVersion,
    firstGroupRowIdx: formatVersion === 'v1' ? firstGroupRowIdxV1 : firstGroupRowIdxV2,
    isMultiLevelBoard: formatVersion === 'v2' ? columnHeadlineIsGreyMultiLevelSubitemsV2 : false,
  };
}

function mondayWorkbookToTransportFormatV1(mondayWorkbook: XLSX.WorkBook): BoardTransportFormat {
  const characteristics = getMondayWorkbookCharacteristics(mondayWorkbook);
  const firstGroupRowIdx = characteristics.hasBoardDescription ? 3 : 1;
  const mondaySheet = mondayWorkbook.Sheets[mondayWorkbook.SheetNames[0]];

  const transportFormat: BoardTransportFormat = {
    name: getBoardName(mondayWorkbook),
    headlines: getRowValues(mondaySheet, 0, firstGroupRowIdx + 1),
    subitemsHeadlines: null,
    items: [],
    subitems: null,
  };

  const range = XLSX.utils.decode_range(mondaySheet['!ref']!);
  const rowsCount = range.e.r + 1;
  let currentGroup = null;

  for (let i = firstGroupRowIdx; i <= rowsCount; i++) {
    const firstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i })];
    const secondCell = mondaySheet[XLSX.utils.encode_cell({ c: 1, r: i })];
    const prevPrevFirstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i - 2 })];
    const prevFirstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i - 1 })];
    const nextFirstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i + 1 })];

    if (!firstCell) {
      continue;
    }

    if (i === firstGroupRowIdx || !prevFirstCell) {
      // group headline
      currentGroup = firstCell.v;
      continue;
    }

    if (i === firstGroupRowIdx + 1) {
      // the item headlines of the first group
      continue;
    }

    if (!prevPrevFirstCell) {
      // item headlines
      continue;
    }

    if (hasFormulasInRow(mondaySheet, i)) {
      // it's a row with summary formulas
      continue;
    }

    if (firstCell.v === 'Subitems' && nextFirstCell && nextFirstCell.v === '' && !hasFormulasInRow(mondaySheet, i + 1)) {
      // subitems headlines
      if (!transportFormat.subitemsHeadlines) {
        transportFormat.subitemsHeadlines = getRowValues(mondaySheet, 1, i);
      }

      continue;
    }

    if (firstCell.v !== '') {
      // item
      transportFormat.items.push(rowToItemTransport(mondaySheet, currentGroup, 0, i, transportFormat.headlines.length));
      continue;
    }

    if (firstCell.v === '' && secondCell && secondCell.v !== '') {
      // subitem
      const subitem = {
        ...rowToItemTransport(mondaySheet, null, 1, i, transportFormat.subitemsHeadlines!.length),
        parentIdx: transportFormat.items.length - 1,
      };
      if (!transportFormat.subitems) {
        transportFormat.subitems = [];
      }

      transportFormat.subitems.push(subitem);
      continue;
    }
  }

  return transportFormat;
}

function getCellLevelMap(mondayWorkbook: XLSX.WorkBook): Map<string, number> {
  const levelMap = new Map<string, number>();

  // Get raw XML content
  const sheetFile = (mondayWorkbook as any).files?.['xl/worksheets/sheet1.xml'];
  if (!sheetFile?.content) return levelMap;

  // Use TextDecoder (browser-compatible) instead of Buffer
  const xmlContent = new TextDecoder('utf-8').decode(sheetFile.content);

  // Build indent lookup from CellXf styles
  const indentByStyleIdx: Record<number, number> = {};
  (mondayWorkbook as any).Styles?.CellXf?.forEach((xf: any, idx: number) => {
    if (xf.alignment?.indent) {
      indentByStyleIdx[idx] = parseInt(xf.alignment.indent);
    }
  });

  // Parse cells from XML to get style indices
  const cellPattern = /<c[^>]+r="([A-Z]+\d+)"[^>]*s="(\d+)"[^>]*>/g;
  let match;
  while ((match = cellPattern.exec(xmlContent)) !== null) {
    const cellAddr = match[1];
    const styleIdx = parseInt(match[2]);
    const indent = indentByStyleIdx[styleIdx] || 0;
    levelMap.set(cellAddr, indent / 2);
  }

  return levelMap;
}

function mondayWorkbookToTransportFormatV2(mondayWorkbook: XLSX.WorkBook): BoardTransportFormat {
  const characteristics = getMondayWorkbookCharacteristics(mondayWorkbook);
  const mondaySheet = mondayWorkbook.Sheets[mondayWorkbook.SheetNames[0]];
  const firstGroupRowIdx = characteristics.firstGroupRowIdx;

  // Build level map only for multi-level boards
  const levelMap = characteristics.isMultiLevelBoard ? getCellLevelMap(mondayWorkbook) : null;

  const transportFormat: BoardTransportFormat = {
    name: getBoardName(mondayWorkbook),
    headlines: getRowValues(mondaySheet, 0, firstGroupRowIdx + 1),
    subitemsHeadlines: null,
    items: [],
    subitems: null,
  };

  const range = XLSX.utils.decode_range(mondaySheet['!ref']!);
  const rowsCount = range.e.r + 1;
  let currentGroup: string | null = null;

  for (let i = firstGroupRowIdx; i <= rowsCount; i++) {
    const firstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i })];
    const secondCell = mondaySheet[XLSX.utils.encode_cell({ c: 1, r: i })];
    const nextFirstCell = mondaySheet[XLSX.utils.encode_cell({ c: 0, r: i + 1 })];

    // Skip null/empty rows
    if (!firstCell) {
      continue;
    }

    // Check for subitems header row FIRST (before summary row check)
    // The "Subitems" row has EFEFEF background on some cells, so we must detect it before skipping summary rows
    if (firstCell.v === 'Subitems' && nextFirstCell && nextFirstCell.v === '' && !isSummaryRowV2(mondaySheet, i + 1)) {
      if (!transportFormat.subitemsHeadlines) {
        transportFormat.subitemsHeadlines = getRowValues(mondaySheet, 1, i);
      }
      continue;
    }

    // Skip summary rows (EFEFEF background and empty first cell)
    if (isSummaryRowV2(mondaySheet, i)) {
      continue;
    }

    // Skip rows with formulas
    // Note: summary rows are already skipped above. In V2, summary fields don't seem to contain formulas anymore.
    //if (hasFormulasInRow(mondaySheet, i)) {
    //  continue;
    //}

    // Check if this is a header row (grey D6D6D6 or EFEFEF background)
    const firstCellBg = _.get(firstCell, 's.fgColor.rgb')?.toUpperCase();
    if (firstCellBg === 'D6D6D6' || firstCellBg === 'EFEFEF') {
      // This is a column headers row - skip it
      continue;
    }

    // Check if this is a group title (next row has grey headers)
    const nextCellBg = _.get(nextFirstCell, 's.fgColor.rgb')?.toUpperCase();
    if (nextCellBg === 'D6D6D6' || nextCellBg === 'EFEFEF') {
      currentGroup = firstCell.v;
      continue;
    }

    // Item row (has non-empty content in first cell)
    if (firstCell.v !== '' && firstCell.v !== undefined) {
      const item = rowToItemTransport(mondaySheet, currentGroup, 0, i, transportFormat.headlines.length);
      if (levelMap) {
        const cellAddr = XLSX.utils.encode_cell({ c: 0, r: i });
        item.level = levelMap.get(cellAddr) ?? 0;
      }
      transportFormat.items.push(item);
      continue;
    }

    // Subitem row (empty first cell, content in second cell)
    if (firstCell.v === '' && secondCell && secondCell.v !== '' && transportFormat.subitemsHeadlines) {
      const subitem = {
        ...rowToItemTransport(mondaySheet, null, 1, i, transportFormat.subitemsHeadlines.length),
        parentIdx: transportFormat.items.length - 1,
      };
      if (!transportFormat.subitems) {
        transportFormat.subitems = [];
      }
      transportFormat.subitems.push(subitem);
      continue;
    }
  }

  return transportFormat;
}

export function mondayWorkbookToTransportFormat(mondayWorkbook: XLSX.WorkBook): BoardTransportFormat {
  const characteristics = getMondayWorkbookCharacteristics(mondayWorkbook);

  if (characteristics.formatVersion === 'v2') {
    return mondayWorkbookToTransportFormatV2(mondayWorkbook);
  }

  if (characteristics.formatVersion === 'v1') {
    return mondayWorkbookToTransportFormatV1(mondayWorkbook);
  }

  throw new Error('This is not a file format that can be converted');
}

function formatExcelCols(json: any[][], maxWidth = 80, minWidth = 10) {
  if (!json.length) {
    return [];
  }

  let widthArr = Object.keys(json[0]).map((key) => {
    return { width: Math.max(key.length + 2, minWidth) }; // plus 2 to account for short object keys
  });

  for (let i = 0; i < json.length; i++) {
    let value = Object.values(json[i]);

    for (let j = 0; j < value.length; j++) {
      if (!widthArr[j]) {
        widthArr[j] = {
          width: minWidth,
        };
      }

      if (value[j] !== null && `${value[j]}`.length > widthArr[j].width) {
        widthArr[j].width = Math.min(`${value[j]}`.length, maxWidth);
      }
    }
  }

  return widthArr;
}

function headlinesToCells(headlines: string[]) {
  return headlines.map((headline) => {
    return { v: (headline || '').toUpperCase(), t: 's', s: { font: { bold: true, name: 'Arial' } } };
  });
}

function formatRow(row: any[]) {
  return row.map((cell) => {
    return { ...cell, s: { font: { name: 'Arial' } } };
  });
}

export function transportFormatToWorkbook(
  transportFormat: BoardTransportFormat,
  includeMainBoard = true,
  includeSubitemsBoard = true,
): XLSX.WorkBook {
  const targetWorkbook = XLSX.utils.book_new();
  const isMultiLevel = transportFormat.items.some((item) => item.level !== undefined);
  const extraColumns = isMultiLevel ? ['Group', 'Level'] : ['Group'];
  const headlineCells = headlinesToCells(transportFormat.headlines.concat(extraColumns));
  const targetSheet = XLSX.utils.aoa_to_sheet([headlineCells]);
  let targetSheetSubitems: XLSX.WorkSheet | null = null;

  if (transportFormat.subitemsHeadlines) {
    const subitemHeadlineCells = headlinesToCells(['Parent Item', ...transportFormat.subitemsHeadlines]);
    targetSheetSubitems = XLSX.utils.aoa_to_sheet([subitemHeadlineCells]);
  }

  transportFormat.items.forEach((item, idx) => {
    const row: any[] = [];

    item.cells.forEach((cell) => {
      row.push(_.pick(cell.original, ['v', 't', 'z', 's']));
    });

    row.push({ v: item.group, t: 's' });
    if (isMultiLevel) {
      row.push({ v: item.level ?? 0, t: 'n' });
    }
    const rowFormatted = formatRow(row);

    XLSX.utils.sheet_add_aoa(targetSheet, [rowFormatted], { origin: -1 });

    if (targetSheetSubitems && transportFormat.subitems) {
      const subitems = transportFormat.subitems.filter((subitem) => subitem.parentIdx === idx);

      subitems.forEach((subitem) => {
        const row: any[] = [];

        row.push({ v: item.name, t: 's' });

        subitem.cells.forEach((cell) => {
          row.push(_.pick(cell.original, ['v', 't', 'z', 's']));
        });

        const rowFormatted = formatRow(row);

        XLSX.utils.sheet_add_aoa(targetSheetSubitems!, [rowFormatted], { origin: -1 });
      });
    }
  });

  const mainSheetName =
    transportFormat.name
      ?.trim()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .substring(0, 20) || 'Sheet1';

  if (includeMainBoard) {
    try {
      targetSheet['!cols'] = formatExcelCols(XLSX.utils.sheet_to_json(targetSheet));
    } catch (err) {}

    targetWorkbook.SheetNames.push(mainSheetName);
    targetWorkbook.Sheets[mainSheetName] = targetSheet;
  }

  if (targetSheetSubitems && includeSubitemsBoard) {
    try {
      targetSheetSubitems['!cols'] = formatExcelCols(XLSX.utils.sheet_to_json(targetSheetSubitems));
    } catch (err) {}

    targetWorkbook.SheetNames.push(mainSheetName + ' Subitems');
    targetWorkbook.Sheets[mainSheetName + ' Subitems'] = targetSheetSubitems;
  }

  return targetWorkbook;
}

export async function fileToWorkbook(file: any): Promise<XLSX.WorkBook> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    let dataLen = 0;

    reader.onload = (e) => {
      try {
        const data = e.target?.result;

        if (!data) {
          reject(
            new Error(
              'The selected file is empty. Make sure nothing is blocking the file reading process (browser extensions, antivirus, security software, etc.).',
            ),
          );
          return;
        }

        if (data instanceof ArrayBuffer) {
          dataLen = data.byteLength;

          if (dataLen === 0) {
            reject(
              new Error(
                'The selected file is empty. Make sure nothing is blocking the file reading process (browser extensions, antivirus, security software, etc.).',
              ),
            );
            return;
          }
        }

        let workbook: XLSX.WorkBook;

        try {
          workbook = XLSX.read(data, { type: 'array', cellStyles: true, bookFiles: true });
        } catch (err) {
          // try again without bookFiles option...
          workbook = XLSX.read(data, { type: 'array', cellStyles: true, bookFiles: false });
        }

        resolve(workbook);
      } catch (err) {
        if (err instanceof Error && err.message) {
          err.message = `${err.message} (size: ${dataLen} bytes)`;
          reject(err);
          return;
        }

        reject(err);
      }
    };

    reader.onerror = (err) => {
      reject(err);
    };
  });
}
